import { useTranslation } from 'react-i18next';
import { Button } from '@c2fo/liquidity';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { EditInvoicesReferrer } from './EditInvoices';

export interface EditInvoicesHeaderStats {
  eligibleInvoiceAmount: number;
  eligibleInvoiceCount: number;
  totalInvoiceCount: number;
}

export interface EditInvoicesHeaderProps {
  headerStats: EditInvoicesHeaderStats;
  onDone: () => void;
  readOnly: boolean;
  referrer: EditInvoicesReferrer;
  resolvedTakerMarket: TakerMarket;
  selectedCurrency: string;
  uniqueBuyerCount: number;
  uniqueCurrencyCount: number;
}

const EditInvoicesHeader = ({
  headerStats,
  onDone,
  readOnly,
  referrer,
  resolvedTakerMarket,
  selectedCurrency,
  uniqueBuyerCount,
  uniqueCurrencyCount,
}: EditInvoicesHeaderProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { asCurrency, asNumber } = useLocaleFormat();

  const handleDone = () => {
    const trackEventName = readOnly ? 'invoices::view-completed' : 'invoices::edit-completed';

    trackEnterpriseEvent(trackEventName, {
      makerOrgUuid: resolvedTakerMarket.makerOrganizationUuid,
      marketUuid: resolvedTakerMarket.marketUuid,
      takerUuid: resolvedTakerMarket.takerDivisionUuid,
      referrer,
    });

    onDone();
  };

  return (
    <div className="sticky top-0 z-10 border-b bg-secondary-500 p-6">
      <div className="flex flex-col justify-between gap-y-1 md:flex-row md:items-center">
        <div className="font-serif text-3xl text-white">{t(readOnly ? 'core.viewInvoices' : 'core.editInvoices')}</div>
        <div className="flex flex-col gap-y-4 md:flex-row md:items-center md:gap-x-8">
          {!readOnly && (
            <>
              <div className="text-white md:text-right">
                <div className="text-2xl font-bold">
                  {asCurrency(headerStats.eligibleInvoiceAmount, selectedCurrency)}
                </div>
                <div className="font-medium md:text-lg">
                  {asNumber({ value: headerStats.eligibleInvoiceCount })}/
                  {t('core.invoiceWithCount', {
                    count: headerStats.totalInvoiceCount,
                    formattedCount: asNumber({ value: headerStats.totalInvoiceCount }),
                  })}{' '}
                  {t('core.included')}
                </div>
                <div>
                  {t('core.customerWithCount', {
                    count: uniqueBuyerCount,
                    formattedCount: asNumber({ value: uniqueBuyerCount }),
                  })}{' '}
                  | {t('core.currencyWithCount', { count: uniqueCurrencyCount })}
                </div>
              </div>
              <div className="h-px w-full self-stretch rounded-full bg-white md:block md:h-auto md:w-px" />
            </>
          )}
          <div>
            <Button className="w-full md:w-auto" inverse onClick={handleDone} variant="primary">
              {t('core.done')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditInvoicesHeader;
